import React from "react";
import { Stack, Typography, Box, Button, Chip, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";

/**
 * Tab 2 de Panel Insights: Coding Opportunities
 * Muestra not addressed vs total, condition types, top HCCs.
 */
export default function TabCoding({ data, loading }) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <Stack spacing={1.5}>
        <Skeleton variant="rounded" height={50} />
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rounded" height={24} />
        ))}
      </Stack>
    );
  }

  if (!data) return null;

  const { totals, conditionTypes, topHCCs } = data;

  return (
    <Stack spacing={2}>
      {/* Summary */}
      <Stack direction="row" spacing={2}>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: "#7C3AED", lineHeight: 1 }}>
            {(totals?.notAddressed || 0).toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>Not Addressed</Typography>
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: "#475569", lineHeight: 1 }}>
            {(totals?.total || 0).toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>Total</Typography>
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: "#16A34A", lineHeight: 1 }}>
            {(totals?.addressed || 0).toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>Addressed</Typography>
        </Box>
      </Stack>

      {/* Condition type chips */}
      {conditionTypes && (
        <Stack spacing={0.5}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.65rem", letterSpacing: 0.5 }}>
            By Condition Type
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap" }}>
            {Object.entries(conditionTypes).map(([type, count]) => (
              <Chip
                key={type}
                label={`${type}: ${count}`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.66rem",
                  fontWeight: 600,
                  bgcolor: type === "Dropped" ? "#FEE2E2" : type === "Suspect" ? "#FEF3C7" : "#F3E8FF",
                  color: type === "Dropped" ? "#991B1B" : type === "Suspect" ? "#92400E" : "#6B21A8",
                }}
              />
            ))}
          </Stack>
        </Stack>
      )}

      {/* Top HCCs */}
      {topHCCs?.length > 0 && (
        <Stack spacing={0.75}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.65rem", letterSpacing: 0.5 }}>
            Top HCCs
          </Typography>
          {topHCCs.slice(0, 5).map((hcc) => (
            <Stack key={hcc.hccId} direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" sx={{ fontSize: "0.7rem", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {hcc.description}
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="caption" fontWeight={600} sx={{ fontSize: "0.68rem", color: "#7C3AED" }}>
                  {hcc.notAddressed}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.62rem" }}>
                  / {hcc.total}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      {/* CTA */}
      <Button
        variant="outlined"
        size="small"
        onClick={() => navigate("/coding-opportunities")}
        sx={{ textTransform: "none", fontSize: "0.75rem", borderRadius: 2, alignSelf: "flex-start", color: "#7C3AED", borderColor: "#7C3AED" }}
      >
        Review Coding Opps
      </Button>
    </Stack>
  );
}
